import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { NEW_THREAD_TITLE } from '../constants';
import type { ConsultoriaThread } from '../services/consultoriaClient';

export type OpsSurface = 'list' | 'panel' | 'memory';

/** The two screens of the memory area: saved memories and procedures (skills). */
export type MemoryView = 'memorias' | 'procedimentos';

/** Local/optimistic thread row for the sidebar (may use pending: ids). */
export type SidebarThreadHint = ConsultoriaThread & {
  /** Stable UI key across pending → real thread_id swap. */
  clientKey: string;
};

interface ConsultoriaChatContextType {
  isThreadListOpen: boolean;
  isPanelOpen: boolean;
  isMemoryOpen: boolean;
  /** Which memory screen is open (meaningful while `isMemoryOpen`). */
  memoryView: MemoryView;
  /** Focused chat: larger panel + blocking dimmed backdrop. */
  isPanelExpanded: boolean;
  /** Undefined = nova conversa (ainda sem thread no backend). */
  activeThreadId: string | undefined;
  panelTitle: string;
  /** Increments on each open/new so the panel remounts session state. */
  sessionNonce: number;
  /** Bumps when the sidebar should re-fetch / merge hints. */
  threadsRefreshToken: number;
  /** Optimistic / session threads so the sidebar updates without full reload. */
  sidebarThreadHints: SidebarThreadHint[];
  /** Selected pending row while the backend thread does not exist yet. */
  pendingSidebarKey: string | undefined;
  openThreadList: () => void;
  closeThreadList: () => void;
  /** Opens a memory screen; no argument (the old entry point) = Memórias. */
  openMemory: (view?: MemoryView) => void;
  closeMemory: () => void;
  openThreadListFromMemory: () => void;
  openNewConversation: () => void;
  openThread: (threadId: string, title?: string) => void;
  closePanel: () => void;
  openThreadListFromPanel: () => void;
  togglePanelExpanded: () => void;
  /** Hide the OPS shell (slides off to the right). No floating minimized FAB. */
  minimizeShell: () => void;
  /** Show the panel restoring whatever conversation/draft it had (launcher). */
  openPanel: () => void;
  /** Launcher: open (restoring the last conversation) or hide. */
  togglePanel: () => void;
  /** Leave fullscreen and go back to the docked panel (conversation kept). */
  exitPanelExpanded: () => void;
  /** True while an answer is in flight (mirrored from the chat session by the shell). */
  assistantBusy: boolean;
  setAssistantBusy: (busy: boolean) => void;
  dismissShell: () => void;
  setActiveThreadId: (threadId: string | undefined) => void;
  setPanelTitle: (title: string) => void;
  /** Ensure an empty chat has a top "Nova conversa" row in the sidebar. */
  ensurePendingSidebarThread: () => void;
  /** After run_started: bind pending sidebar row to real thread id. */
  bindSidebarThreadId: (threadId: string) => void;
  /** After thread_title / rename: update active row title in the sidebar. */
  updateSidebarThreadTitle: (title: string, threadId?: string) => void;
  removeSidebarThread: (threadId: string) => void;
  bumpThreadsRefresh: () => void;
  /** Composer draft — survives hide/remount of the panel. */
  composerDraft: string;
  setComposerDraft: (value: string) => void;
}

const ConsultoriaChatContext = createContext<ConsultoriaChatContextType | undefined>(undefined);

export const useConsultoriaChat = (): ConsultoriaChatContextType => {
  const ctx = useContext(ConsultoriaChatContext);
  if (!ctx) {
    throw new Error('useConsultoriaChat deve ser usado dentro de ConsultoriaChatProvider');
  }
  return ctx;
};

/**
 * Assinatura estreita para telas fora do shell (ex.: atalho "Pedir análise ao Otto" e o modal
 * da máquina). Não muda a cada tecla do composer (não carrega `composerDraft`), então telas
 * pesadas não re-renderizam enquanto o usuário digita no Otto.
 */
export interface ConsultoriaShellStatus {
  isPanelOpen: boolean;
  isPanelExpanded?: boolean;
  assistantBusy: boolean;
  openNewConversation: () => void;
  openPanel: () => void;
  setComposerDraft: (value: string) => void;
}

const ConsultoriaShellStatusContext = createContext<ConsultoriaShellStatus | undefined>(undefined);

/** `undefined` fora do `ConsultoriaChatProvider` (chat não montado): quem usa se esconde. */
export const useOptionalConsultoriaShellStatus = (): ConsultoriaShellStatus | undefined =>
  useContext(ConsultoriaShellStatusContext);

const nowIso = () => new Date().toISOString();

export const ConsultoriaChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isThreadListOpen, setIsThreadListOpen] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isMemoryOpen, setIsMemoryOpen] = useState(false);
  const [memoryView, setMemoryView] = useState<MemoryView>('memorias');
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>(undefined);
  const [panelTitle, setPanelTitle] = useState(NEW_THREAD_TITLE);
  const [sessionNonce, setSessionNonce] = useState(0);
  const [composerDraft, setComposerDraft] = useState('');
  const [threadsRefreshToken, setThreadsRefreshToken] = useState(0);
  const [sidebarThreadHints, setSidebarThreadHints] = useState<SidebarThreadHint[]>([]);
  const [pendingSidebarKey, setPendingSidebarKey] = useState<string | undefined>(undefined);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const activeThreadIdRef = useRef(activeThreadId);
  const pendingSidebarKeyRef = useRef(pendingSidebarKey);
  activeThreadIdRef.current = activeThreadId;
  pendingSidebarKeyRef.current = pendingSidebarKey;

  const bumpThreadsRefresh = useCallback(() => {
    setThreadsRefreshToken((n) => n + 1);
  }, []);

  const openThreadList = useCallback(() => {
    setIsMemoryOpen(false);
    setIsPanelOpen(true);
    setIsThreadListOpen(true);
    setThreadsRefreshToken((n) => n + 1);
  }, []);

  const closeThreadList = useCallback(() => {
    setIsThreadListOpen(false);
  }, []);

  const openMemory = useCallback((view?: MemoryView) => {
    // Tolerates being used directly as an onClick (the event is not a view).
    setMemoryView(view === 'procedimentos' ? 'procedimentos' : 'memorias');
    setIsThreadListOpen(false);
    setIsPanelOpen(true);
    setIsMemoryOpen(true);
  }, []);

  const closeMemory = useCallback(() => {
    setIsMemoryOpen(false);
  }, []);

  const openThreadListFromMemory = useCallback(() => {
    setIsMemoryOpen(false);
    setIsPanelOpen(true);
    setIsThreadListOpen(true);
    setThreadsRefreshToken((n) => n + 1);
  }, []);

  const openNewConversation = useCallback(() => {
    const clientKey = `pending:${Date.now()}`;
    const hint: SidebarThreadHint = {
      clientKey,
      thread_id: clientKey,
      title: NEW_THREAD_TITLE,
      summary: '',
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    setSidebarThreadHints((prev) => {
      const withoutDupePending = prev.filter((t) => !t.thread_id.startsWith('pending:'));
      return [hint, ...withoutDupePending];
    });
    setPendingSidebarKey(clientKey);
    pendingSidebarKeyRef.current = clientKey;
    setActiveThreadId(undefined);
    activeThreadIdRef.current = undefined;
    setPanelTitle(NEW_THREAD_TITLE);
    setComposerDraft('');
    setSessionNonce((n) => n + 1);
    setIsMemoryOpen(false);
    setIsPanelOpen(true);
    setThreadsRefreshToken((n) => n + 1);
    // Dock: leave list; expanded: keep sidebar open beside the chat.
    setIsPanelExpanded((expanded) => {
      if (!expanded) setIsThreadListOpen(false);
      return expanded;
    });
  }, []);

  const openThread = useCallback((threadId: string, title?: string) => {
    if (threadId.startsWith('pending:')) {
      setPendingSidebarKey(threadId);
      pendingSidebarKeyRef.current = threadId;
      setActiveThreadId(undefined);
      activeThreadIdRef.current = undefined;
      setPanelTitle(NEW_THREAD_TITLE);
      setComposerDraft('');
      setSessionNonce((n) => n + 1);
      setIsMemoryOpen(false);
      setIsPanelOpen(true);
      setIsPanelExpanded((expanded) => {
        if (!expanded) setIsThreadListOpen(false);
        return expanded;
      });
      return;
    }
    setPendingSidebarKey(undefined);
    pendingSidebarKeyRef.current = undefined;
    setActiveThreadId(threadId);
    activeThreadIdRef.current = threadId;
    setPanelTitle((title || 'Conversa').trim() || 'Conversa');
    setComposerDraft('');
    setSessionNonce((n) => n + 1);
    setIsMemoryOpen(false);
    setIsPanelOpen(true);
    setIsPanelExpanded((expanded) => {
      if (!expanded) setIsThreadListOpen(false);
      return expanded;
    });
  }, []);

  const ensurePendingSidebarThread = useCallback(() => {
    if (activeThreadIdRef.current || pendingSidebarKeyRef.current) return;
    const clientKey = `pending:${Date.now()}`;
    setPendingSidebarKey(clientKey);
    pendingSidebarKeyRef.current = clientKey;
    setSidebarThreadHints((prev) => {
      if (prev.some((t) => t.thread_id.startsWith('pending:'))) return prev;
      return [
        {
          clientKey,
          thread_id: clientKey,
          title: NEW_THREAD_TITLE,
          summary: '',
          created_at: nowIso(),
          updated_at: nowIso(),
        },
        ...prev,
      ];
    });
  }, []);

  const bindSidebarThreadId = useCallback((threadId: string) => {
    const realId = threadId.trim();
    if (!realId || realId.startsWith('pending:')) return;
    const pendingKey = pendingSidebarKeyRef.current;
    setSidebarThreadHints((prev) => {
      const pending =
        (pendingKey ? prev.find((t) => t.clientKey === pendingKey) : undefined) ||
        prev.find((t) => t.thread_id.startsWith('pending:'));
      if (pending) {
        return prev.map((t) =>
          t.clientKey === pending.clientKey
            ? { ...t, thread_id: realId, updated_at: nowIso() }
            : t,
        );
      }
      if (prev.some((t) => t.thread_id === realId)) {
        return prev.map((t) =>
          t.thread_id === realId ? { ...t, updated_at: nowIso() } : t,
        );
      }
      return [
        {
          clientKey: realId,
          thread_id: realId,
          title: NEW_THREAD_TITLE,
          summary: '',
          created_at: nowIso(),
          updated_at: nowIso(),
        },
        ...prev.filter((t) => !t.thread_id.startsWith('pending:')),
      ];
    });
    setPendingSidebarKey(undefined);
    pendingSidebarKeyRef.current = undefined;
    setActiveThreadId(realId);
    activeThreadIdRef.current = realId;
    setThreadsRefreshToken((n) => n + 1);
  }, []);

  const updateSidebarThreadTitle = useCallback((title: string, threadId?: string) => {
    const clean = title.trim().slice(0, 64);
    if (!clean) return;
    const targetId = threadId || activeThreadIdRef.current;
    const pendingKey = pendingSidebarKeyRef.current;
    setSidebarThreadHints((prev) => {
      let matched = false;
      const next = prev.map((t) => {
        const match =
          (targetId && t.thread_id === targetId) ||
          (!targetId && pendingKey && t.clientKey === pendingKey) ||
          (!targetId && !pendingKey && t.thread_id.startsWith('pending:'));
        if (!match) return t;
        matched = true;
        return { ...t, title: clean, updated_at: nowIso() };
      });
      if (matched || !targetId) return next;
      // Thread exists in session but hint missing (e.g. race) — upsert.
      return [
        {
          clientKey: targetId,
          thread_id: targetId,
          title: clean,
          summary: '',
          created_at: nowIso(),
          updated_at: nowIso(),
        },
        ...next,
      ];
    });
  }, []);

  const removeSidebarThread = useCallback((threadId: string) => {
    setSidebarThreadHints((prev) =>
      prev.filter((t) => t.thread_id !== threadId && t.clientKey !== threadId),
    );
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    setIsPanelExpanded(false);
    setIsThreadListOpen(false);
    setIsMemoryOpen(false);
  }, []);

  const openThreadListFromPanel = useCallback(() => {
    setIsMemoryOpen(false);
    setIsPanelOpen(true);
    setIsThreadListOpen((open) => {
      const next = !open;
      if (next) setThreadsRefreshToken((n) => n + 1);
      return next;
    });
  }, []);

  const togglePanelExpanded = useCallback(() => {
    // Expanding/collapsing never swaps the conversation for another view: the
    // wide fullscreen shows history as a sidebar on its own (P1 task 5).
    setIsPanelExpanded((prev) => {
      const next = !prev;
      if (next) {
        setIsMemoryOpen(false);
        setThreadsRefreshToken((n) => n + 1);
      }
      setIsThreadListOpen(false);
      return next;
    });
  }, []);

  const exitPanelExpanded = useCallback(() => {
    setIsPanelExpanded(false);
    setIsThreadListOpen(false);
  }, []);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const togglePanel = useCallback(() => {
    setIsPanelOpen((open) => {
      if (open) {
        setIsThreadListOpen(false);
        setIsMemoryOpen(false);
        setIsPanelExpanded(false);
      }
      return !open;
    });
  }, []);

  const minimizeShell = useCallback(() => {
    setIsPanelOpen(false);
    setIsThreadListOpen(false);
    setIsMemoryOpen(false);
    setIsPanelExpanded(false);
  }, []);

  const dismissShell = useCallback(() => {
    minimizeShell();
  }, [minimizeShell]);

  const value = useMemo(
    () => ({
      isThreadListOpen,
      isPanelOpen,
      isMemoryOpen,
      memoryView,
      isPanelExpanded,
      activeThreadId,
      panelTitle,
      sessionNonce,
      threadsRefreshToken,
      sidebarThreadHints,
      pendingSidebarKey,
      openThreadList,
      closeThreadList,
      openMemory,
      closeMemory,
      openThreadListFromMemory,
      openNewConversation,
      openThread,
      closePanel,
      openThreadListFromPanel,
      togglePanelExpanded,
      minimizeShell,
      dismissShell,
      openPanel,
      togglePanel,
      exitPanelExpanded,
      assistantBusy,
      setAssistantBusy,
      setActiveThreadId,
      setPanelTitle,
      bindSidebarThreadId,
      updateSidebarThreadTitle,
      removeSidebarThread,
      bumpThreadsRefresh,
      ensurePendingSidebarThread,
      composerDraft,
      setComposerDraft,
    }),
    [
      isThreadListOpen,
      isPanelOpen,
      isMemoryOpen,
      memoryView,
      isPanelExpanded,
      activeThreadId,
      panelTitle,
      sessionNonce,
      threadsRefreshToken,
      sidebarThreadHints,
      pendingSidebarKey,
      composerDraft,
      assistantBusy,
      openPanel,
      togglePanel,
      exitPanelExpanded,
      openThreadList,
      closeThreadList,
      openMemory,
      closeMemory,
      openThreadListFromMemory,
      openNewConversation,
      openThread,
      closePanel,
      openThreadListFromPanel,
      togglePanelExpanded,
      minimizeShell,
      dismissShell,
      bindSidebarThreadId,
      updateSidebarThreadTitle,
      removeSidebarThread,
      bumpThreadsRefresh,
      ensurePendingSidebarThread,
    ],
  );

  const shellStatus = useMemo<ConsultoriaShellStatus>(
    () => ({ isPanelOpen, isPanelExpanded, assistantBusy, openNewConversation, openPanel, setComposerDraft }),
    [isPanelOpen, isPanelExpanded, assistantBusy, openNewConversation, openPanel],
  );

  return (
    <ConsultoriaChatContext.Provider value={value}>
      <ConsultoriaShellStatusContext.Provider value={shellStatus}>{children}</ConsultoriaShellStatusContext.Provider>
    </ConsultoriaChatContext.Provider>
  );
};
